import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { AutoCoderImportBatch } from '../types/gotypes'
import { extractErrorMessage } from '../utils/apitools'
import { JOB_RECEIPTS_QUERY_KEY } from './useJobReceipts'

/**
 * The three manual doors into a job's data (the plan's Interface 2):
 * `POST /autocoder/:customer/:job/ingest/{csv,apify,carbon}`.
 *
 * All three are JOB-scoped and pipeline-less — a fetch's whole job is to turn
 * what came back into a receipt plus records, and sending those records into a
 * coding pipeline is a separate, later, deliberate act. All three answer with
 * the receipt (`{batch}`), so the caller never has to guess which row in the
 * ledger is the one it just created.
 *
 * CSV and Carbon are synchronous: the answer is a FINISHED receipt. Apify is
 * asynchronous: the answer is an OPEN one (`finalizedAt` absent), and the
 * receipts list is the progress UI from there.
 *
 * A fetch that failed is DATA, not a transport error — it comes back 200 with
 * `error` set on the receipt — so these mutations only reject on a malformed
 * request or a broken connection.
 *
 * (`useRetryReceipt`, the fourth ingest door, lives beside the list it
 * invalidates in `useJobReceipts.ts`.)
 */

/** What a manual Apify fetch asks for — mirrors `apifyIngestRequest` (Go). */
export interface ApifyIngestRequest {
  /** tiktok | instagram | youtube | facebook. A hint; the URL is the real identity. */
  platform: string
  url: string
  maxComments?: number
  includeReplies?: boolean
}

/** What a manual Carbon fetch asks for — mirrors `carbonIngestRequest` (Go). */
export interface CarbonIngestRequest {
  variable: string
  filter?: string
}

export interface UseIngestSourcesResult {
  ingestCsv: (file: File) => Promise<AutoCoderImportBatch | undefined>
  ingestApify: (request: ApifyIngestRequest) => Promise<AutoCoderImportBatch | undefined>
  ingestCarbon: (request: CarbonIngestRequest) => Promise<AutoCoderImportBatch | undefined>
  /** True while ANY of the three is in flight — the submit gate. */
  pending: boolean
  error: string | null
}

interface IngestResponse {
  batch: AutoCoderImportBatch
}

export const useIngestSources = (customer: string, job: string): UseIngestSourcesResult => {
  const queryClient = useQueryClient()

  // Prefix invalidation: a new receipt lands at the TOP of the ledger, so it
  // belongs to every page size and page the user might be looking at, and the
  // mutation cannot know which one that is.
  const invalidateReceipts = () => {
    void queryClient.invalidateQueries({ queryKey: [JOB_RECEIPTS_QUERY_KEY] })
  }

  const csvMutation = useMutation<AutoCoderImportBatch | undefined, Error, File>({
    mutationFn: async file => {
      const formData = new FormData()
      formData.append('file', file)
      try {
        const response = await axios.post<IngestResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/ingest/csv`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        return response.data?.batch
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    onSuccess: invalidateReceipts,
  })

  const apifyMutation = useMutation<AutoCoderImportBatch | undefined, Error, ApifyIngestRequest>({
    mutationFn: async request => {
      try {
        const response = await axios.post<IngestResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/ingest/apify`,
          request,
        )
        return response.data?.batch
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    onSuccess: invalidateReceipts,
  })

  const carbonMutation = useMutation<AutoCoderImportBatch | undefined, Error, CarbonIngestRequest>({
    mutationFn: async request => {
      try {
        const response = await axios.post<IngestResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/ingest/carbon`,
          request,
        )
        return response.data?.batch
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    onSuccess: invalidateReceipts,
  })

  const firstError = csvMutation.error || apifyMutation.error || carbonMutation.error

  return {
    ingestCsv: file => csvMutation.mutateAsync(file),
    ingestApify: request => apifyMutation.mutateAsync(request),
    ingestCarbon: request => carbonMutation.mutateAsync(request),
    pending: csvMutation.isPending || apifyMutation.isPending || carbonMutation.isPending,
    error: firstError ? firstError.message : null,
  }
}

export default useIngestSources
